import React, { useEffect, useState } from 'react';
import { CheckCircle, X, Heart } from 'lucide-react';

export interface ToastProps {
  message: string;
  type?: 'success' | 'info' | 'error';
  duration?: number;
  onClose: () => void;
  icon?: 'check' | 'heart';
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'success',
  duration = 3000,
  onClose,
  icon = 'check',
  action
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onClose, 300); // Wait for exit animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  };

  const bgColor = {
    success: 'bg-green-50 border-green-200',
    info: 'bg-rm-bg-50 border-rm-accent-300',
    error: 'bg-red-50 border-red-200'
  }[type];

  const textColor = {
    success: 'text-green-800',
    info: 'text-rm-primary-700',
    error: 'text-red-800'
  }[type];

  const iconColor = {
    success: 'text-green-500',
    info: 'text-rm-accent-600',
    error: 'text-red-500'
  }[type];

  const IconComponent = icon === 'heart' ? Heart : CheckCircle;

  return (
    <div
      className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-out ${
        isExiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
      }`}
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-card border shadow-card ${bgColor}`}
      >
        <IconComponent className={`w-5 h-5 ${iconColor} flex-shrink-0`} />
        <span className={`text-sm font-medium ${textColor}`}>{message}</span>
        
        {action && (
          <button
            onClick={action.onClick}
            className="text-sm font-medium text-rm-accent-600 hover:text-rm-accent-500 underline ml-2"
          >
            {action.label}
          </button>
        )}
        
        <button
          onClick={handleClose}
          className="ml-2 text-rm-neutral-600 hover:text-rm-ink-900 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
